import React from 'react';
import { useRef } from 'react';
import StockOperatorLineCard from './StockOperatorLineCard';

const filterChips = [
  { key: 'all', label: 'Todos' },
  { key: 'pending', label: 'Pend.' },
  { key: 'checked_ok', label: 'OK' },
  { key: 'checked_diff', label: 'Dif.' },
  { key: 'added_during_control', label: 'Agreg.' },
];

const differenceTone = (value) => {
  if (value == null || Number.isNaN(Number(value))) return 'text-stone-500';
  if (Number(value) === 0) return 'text-emerald-700';
  return Number(value) > 0 ? 'text-amber-700' : 'text-rose-700';
};

const differenceLabel = (value) => {
  if (value == null || Number.isNaN(Number(value))) return '—';
  if (Number(value) === 0) return '0';
  return Number(value) > 0 ? `+${value}` : `${value}`;
};

const MiniIndicator = ({ label, value, tone = 'text-stone-700', bg = 'bg-stone-100', border = 'border-stone-200' }) => (
  <div className={`inline-flex items-center gap-2 rounded-full border ${border} ${bg} px-2.5 py-1 text-[11px] font-semibold`}>
    <span className="text-stone-500 uppercase tracking-[0.12em]">{label}</span>
    <span className={tone}>{value}</span>
  </div>
);

const CompactActionButton = ({ children, onClick, disabled, className = '' }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    className={`inline-flex items-center justify-center rounded-xl px-3 py-2 text-xs font-bold transition-colors ${className}`}
  >
    {children}
  </button>
);

const StockOperatorSheetView = ({
  sheet,
  lines,
  searchTerm,
  setSearchTerm,
  lineFilter,
  setLineFilter,
  onBack,
  onShowHistory,
  onInlineChange,
  onOpenAddProduct,
  onSaveSheet,
  onFinalizeSheet,
  saving = false,
  Icon,
  hideAddProduct = false,
  hideScan = false,
  backLabel = 'Volver a control',
  readOnly = false,
  onSheetMetaChange,
  adjustments = [],
  onOpenAdjustment,
}) => {
  const physicalInputRefs = useRef({});
  const observationRefs = useRef({});

  const focusNextPhysicalInput = (currentLineId) => {
    const currentIndex = lines.findIndex((line) => line.id === currentLineId);
    if (currentIndex === -1) return;
    const nextLine = lines[currentIndex + 1];
    if (!nextLine) return;
    const nextInput = physicalInputRefs.current[nextLine.id];
    if (nextInput && typeof nextInput.focus === 'function') {
      nextInput.focus();
      nextInput.select?.();
    }
  };

  const handlePhysicalInputEnter = (event, line) => {
    if (event.key !== 'Enter') return;
    event.preventDefault();
    const currentValue = event.currentTarget.value === '' ? null : Number(event.currentTarget.value);
    const expectedValue = line.expectedQuantity == null ? null : Number(line.expectedQuantity);
    const differenceValue = currentValue == null || expectedValue == null ? null : currentValue - expectedValue;
    if (differenceValue != null && differenceValue !== 0) {
      const observationField = observationRefs.current[line.id];
      if (observationField && typeof observationField.focus === 'function') {
        observationField.focus();
        observationField.select?.();
      }
      return;
    }
    focusNextPhysicalInput(line.id);
  };

  const handleObservationEnter = (event, line) => {
    if (event.key !== 'Enter' || event.shiftKey) return;
    event.preventDefault();
    focusNextPhysicalInput(line.id);
  };

  const toggleTaggedLine = (lineId) => {
    if (!onSheetMetaChange) return;
    const currentTagged = Array.isArray(sheet.generalTaggedLineIds) ? sheet.generalTaggedLineIds : [];
    const nextTagged = currentTagged.includes(lineId)
      ? currentTagged.filter((id) => id !== lineId)
      : [...currentTagged, lineId];
    onSheetMetaChange({ generalTaggedLineIds: nextTagged });
  };

  const generalObservationValue = String(sheet.generalObservation || '').trim();
  const generalTaggedLineIds = Array.isArray(sheet.generalTaggedLineIds) ? sheet.generalTaggedLineIds : [];
  const taggedLines = lines.filter((line) => generalTaggedLineIds.includes(line.id));
  const differenceLines = lines.filter((line) => line.differenceValue != null && Number(line.differenceValue) !== 0);

  return (
    <div className="flex flex-col gap-3">
      <div className="sticky top-0 z-20 overflow-hidden rounded-2xl border border-stone-200 bg-white/95 shadow-sm backdrop-blur">
        <div className="border-b border-stone-200 px-3 py-2 md:px-4">
          <div className="flex flex-col gap-2 xl:flex-row xl:items-center xl:justify-between">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <button
                  onClick={onBack}
                  className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-xl border border-stone-200 bg-white text-stone-600 hover:bg-stone-50"
                  title={backLabel}
                >
                  <Icon path="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" className="h-4 w-4" />
                </button>
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="truncate text-sm font-bold text-stone-900">{sheet.title}</p>
                    <span className="hidden rounded-full bg-blue-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-blue-700 sm:inline-flex">
                      {readOnly ? 'Histórico cerrado' : 'Planilla'}
                    </span>
                  </div>
                  <p className="truncate text-[11px] text-stone-500">{sheet.locationLabel} • {sheet.dateLabel}</p>
                </div>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2 xl:justify-end">
              <MiniIndicator label="Items" value={sheet.totalLines} />
              <MiniIndicator label="Pend" value={sheet.pendingLines} tone="text-amber-700" bg="bg-amber-50" border="border-amber-200" />
              <MiniIndicator label="OK" value={sheet.completedLines} tone="text-emerald-700" bg="bg-emerald-50" border="border-emerald-200" />
              <MiniIndicator label="Dif" value={sheet.diffLines} tone="text-rose-700" bg="bg-rose-50" border="border-rose-200" />
            </div>
          </div>
        </div>

        <div className="px-3 py-2 md:px-4">
          <div className="flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex min-w-0 flex-1 items-center gap-2">
              <input
                type="text"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                placeholder="Buscar producto"
                className="min-w-0 flex-1 rounded-xl border border-stone-300 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500"
              />

              {!hideAddProduct ? (
                <CompactActionButton
                  onClick={onOpenAddProduct}
                  className="border border-stone-300 bg-white text-stone-700 hover:bg-stone-50"
                >
                  + Producto
                </CompactActionButton>
              ) : null}

              {!hideScan ? (
                <CompactActionButton
                  disabled
                  className="cursor-not-allowed bg-blue-600 text-white opacity-70"
                >
                  Escanear
                </CompactActionButton>
              ) : null}
            </div>

            <div className="flex flex-wrap items-center gap-2 lg:justify-end">
              {readOnly ? (
                <div className="flex flex-wrap items-center justify-end gap-2">
                  <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-xs font-semibold text-slate-600">
                    Esta planilla ya fue finalizada y quedó solo para consulta.
                  </div>
                  {differenceLines.length > 0 ? (
                    <CompactActionButton
                      onClick={onOpenAdjustment}
                      className="border border-amber-300 bg-amber-50 text-amber-900 hover:bg-amber-100"
                    >
                      Registrar ajuste
                    </CompactActionButton>
                  ) : null}
                </div>
              ) : (
                <>
                  <CompactActionButton
                    onClick={onSaveSheet}
                    disabled={saving}
                    className={saving ? 'cursor-not-allowed border border-stone-200 bg-stone-100 text-stone-400' : 'border border-cyan-300 bg-cyan-50 text-cyan-800 hover:bg-cyan-100'}
                  >
                    Guardar
                  </CompactActionButton>
                  <CompactActionButton
                    onClick={onFinalizeSheet}
                    disabled={saving}
                    className={saving ? 'cursor-not-allowed border border-stone-200 bg-stone-100 text-stone-400' : 'border border-emerald-300 bg-emerald-50 text-emerald-800 hover:bg-emerald-100'}
                  >
                    Finalizar
                  </CompactActionButton>
                </>
              )}
            </div>
          </div>

          <div className="mt-2 flex gap-2 overflow-x-auto pb-0.5">
            {filterChips.map((chip) => {
              const isActive = chip.key === lineFilter;
              return (
                <button
                  key={chip.key}
                  onClick={() => setLineFilter(chip.key)}
                  className={`shrink-0 rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${isActive ? 'bg-stone-900 text-white' : 'bg-stone-100 text-stone-600 hover:bg-stone-200'}`}
                >
                  {chip.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {lines.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-stone-300 bg-white p-8 text-center text-stone-500 shadow-sm">
          No hay líneas para mostrar con ese filtro.
        </div>
      ) : (
        <>
          <div className={`rounded-3xl border px-4 py-4 shadow-sm ${readOnly ? 'border-slate-200 bg-slate-50' : 'border-cyan-200 bg-cyan-50/60'}`}>
            <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
              <div className="min-w-0 flex-1">
                <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Observación general del control</p>
                {readOnly ? (
                  <div className="mt-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700">
                    {generalObservationValue || 'Sin observación general asentada en esta planilla.'}
                  </div>
                ) : (
                  <textarea
                    rows={3}
                    value={sheet.generalObservation || ''}
                    onChange={(event) => onSheetMetaChange?.({ generalObservation: event.target.value })}
                    className="mt-2 min-h-[92px] w-full rounded-2xl border border-cyan-200 bg-white px-4 py-3 text-sm text-slate-700 outline-none transition focus:border-cyan-400 focus:ring-2 focus:ring-cyan-500/20"
                    placeholder="Opcional: dejá asentado el contexto general del control, por ejemplo un mal conteo, mercadería mezclada o una revisión pendiente para la próxima planilla."
                  />
                )}
              </div>
              <div className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 lg:max-w-sm">
                <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Filas marcadas para seguimiento</p>
                {readOnly ? (
                  taggedLines.length > 0 ? (
                    <div className="mt-2 flex flex-wrap gap-2">
                      {taggedLines.map((line) => (
                        <span key={line.id} className="rounded-full border border-amber-200 bg-amber-50 px-2.5 py-1 text-xs font-semibold text-amber-800">
                          {line.productName}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <p className="mt-2 text-sm text-slate-500">No quedaron filas marcadas en esta planilla.</p>
                  )
                ) : differenceLines.length > 0 ? (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {differenceLines.map((line) => {
                      const isTagged = generalTaggedLineIds.includes(line.id);
                      return (
                        <button
                          key={line.id}
                          type="button"
                          onClick={() => toggleTaggedLine(line.id)}
                          className={`rounded-full border px-2.5 py-1 text-xs font-semibold transition-colors ${isTagged ? 'border-amber-300 bg-amber-100 text-amber-900' : 'border-slate-200 bg-slate-50 text-slate-600 hover:bg-slate-100'}`}
                        >
                          {line.productName}
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  <p className="mt-2 text-sm text-slate-500">Las filas se pueden marcar cuando aparece una diferencia.</p>
                )}
              </div>
            </div>
          </div>

          {readOnly && adjustments.length > 0 ? (
            <div className="rounded-3xl border border-amber-200 bg-amber-50/70 px-4 py-4 shadow-sm">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-amber-700">Ajustes asentados</p>
                  <p className="mt-1 text-sm text-amber-900">
                    Estos ajustes ya quedaron registrados como eventos aparte. La planilla original no se reabre.
                  </p>
                </div>
                <span className="rounded-full border border-amber-300 bg-white px-3 py-1 text-xs font-bold text-amber-800">
                  {adjustments.length} ajuste{adjustments.length === 1 ? '' : 's'}
                </span>
              </div>
              <div className="mt-3 space-y-2">
                {adjustments.map((adjustment) => (
                  <div key={adjustment.id} className="rounded-2xl border border-amber-200 bg-white px-3 py-3">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <p className="text-sm font-semibold text-slate-900">
                        {adjustment.productName || 'Producto'} · {adjustment.adjustmentDelta > 0 ? `+${adjustment.adjustmentDelta}` : adjustment.adjustmentDelta}
                      </p>
                      <span className="text-xs font-medium text-slate-500">
                        {typeof adjustment?.endTime?.toDate === 'function'
                          ? adjustment.endTime.toDate().toLocaleString('es-AR')
                          : adjustment?.createdAt?.toDate?.()?.toLocaleString?.('es-AR') || 'Sin fecha'}
                      </span>
                    </div>
                    <p className="mt-1 text-xs font-semibold uppercase tracking-[0.12em] text-amber-700">
                      {adjustment.adjustmentReasonSnapshot || 'Ajuste asentado'}
                    </p>
                    {adjustment.adjustmentNoteSnapshot ? (
                      <p className="mt-1 text-sm text-slate-600">{adjustment.adjustmentNoteSnapshot}</p>
                    ) : null}
                    <p className="mt-1 text-xs text-slate-500">
                      {adjustment.adjustedByName || adjustment.controllerDisplayNameSnapshot || 'Sin responsable visible'}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          ) : null}

          <div className="hidden overflow-hidden rounded-3xl border border-stone-200 bg-white shadow-sm lg:block">
            <div className="grid grid-cols-[minmax(0,2.2fr)_120px_120px_120px_minmax(220px,1.6fr)_110px] gap-0 border-b border-stone-200 bg-stone-50 px-4 py-3 text-xs font-bold uppercase tracking-[0.16em] text-stone-500">
              <div>Producto</div>
              <div>Sistema</div>
              <div>Físico</div>
              <div>Diferencia</div>
              <div>Observación</div>
              <div className="text-right">Controles</div>
            </div>
            <div className="divide-y divide-stone-100">
              {lines.map((line) => (
                <div key={line.id} className="grid grid-cols-[minmax(0,2.2fr)_120px_120px_120px_minmax(220px,1.6fr)_110px] items-start gap-0 px-4 py-3">
                  <div className="pr-4">
                    <p className="font-semibold text-stone-900">{line.productName}</p>
                    <p className="mt-1 text-xs text-stone-500">{line.codeLabel || 'Sin variante visible'}</p>
                    {line.categoryLabel ? <p className="mt-1 text-[11px] uppercase tracking-[0.12em] text-stone-400">{line.categoryLabel}</p> : null}
                  </div>
                  <div className="pr-3">
                    <div className="rounded-2xl border border-stone-200 bg-stone-50 px-3 py-2 text-center text-base font-bold text-stone-800">
                      {line.expectedLabel}
                    </div>
                  </div>
                  <div className="pr-3">
                    <input
                      ref={(node) => {
                        if (node) {
                          physicalInputRefs.current[line.id] = node;
                        } else {
                          delete physicalInputRefs.current[line.id];
                        }
                      }}
                      type="number"
                      min="0"
                      value={line.countedQuantity == null ? '' : line.countedQuantity}
                      onChange={(event) => onInlineChange?.(line.id, { countedQuantity: event.target.value === '' ? null : Number(event.target.value) })}
                      onKeyDown={(event) => handlePhysicalInputEnter(event, line)}
                      disabled={readOnly}
                      className={`w-full rounded-2xl border px-3 py-2 text-center text-base font-bold outline-none ${readOnly ? 'cursor-not-allowed border-stone-200 bg-stone-100 text-stone-500' : 'border-cyan-200 bg-cyan-50 text-stone-900 focus:ring-2 focus:ring-cyan-500'}`}
                      placeholder="0"
                    />
                  </div>
                  <div className="pr-3">
                    <div className={`rounded-2xl border px-3 py-2 text-center text-base font-bold ${line.status === 'checked_diff' ? 'border-rose-200 bg-rose-50' : line.status === 'checked_ok' ? 'border-emerald-200 bg-emerald-50' : 'border-stone-200 bg-stone-50'} ${differenceTone(line.differenceValue)}`}>
                      {differenceLabel(line.differenceValue)}
                    </div>
                  </div>
                  <div className="pr-3">
                    <textarea
                      ref={(node) => {
                        if (node) {
                          observationRefs.current[line.id] = node;
                        } else {
                          delete observationRefs.current[line.id];
                        }
                      }}
                      value={line.noteLabel === 'Sin observación' ? '' : line.noteLabel}
                      onChange={(event) => onInlineChange?.(line.id, { noteLabel: event.target.value })}
                      onKeyDown={(event) => handleObservationEnter(event, line)}
                      rows={2}
                      disabled={readOnly || line.differenceValue === 0 || line.differenceValue == null}
                      className={`w-full resize-none rounded-2xl border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-cyan-500 ${line.differenceValue === 0 || line.differenceValue == null ? 'cursor-not-allowed border-stone-200 bg-stone-100 text-stone-400 placeholder:text-stone-400' : 'border-stone-200 bg-white text-stone-700'}`}
                      placeholder={line.differenceValue === 0 || line.differenceValue == null ? 'Se habilita si hay diferencia' : 'Motivo de la diferencia'}
                    />
                  </div>
                  <div className="flex justify-end">
                    <button
                      onClick={() => onShowHistory(line.product)}
                      className="rounded-xl border border-stone-300 bg-white px-3 py-2 text-sm font-semibold text-stone-700 hover:bg-stone-50 transition-colors"
                    >
                      Ver
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-3 lg:hidden">
            {lines.map((line) => (
              <StockOperatorLineCard
                key={line.id}
                line={line}
                onShowHistory={onShowHistory}
                onInlineChange={onInlineChange}
                editable={!readOnly}
                Icon={Icon}
                onAdvanceToNextLine={focusNextPhysicalInput}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default StockOperatorSheetView;
